/**
 * Halo Ring Sizer — AI-powered ring sizing tool
 * Uses MediaPipe Hand Landmarker + credit card calibration
 */
(function () {
  'use strict';

  /* ============================================================
     CONSTANTS
     ============================================================ */
  var CARD_WIDTH_MM = 85.6;
  var CARD_HEIGHT_MM = 53.98;
  var CARD_ASPECT = CARD_WIDTH_MM / CARD_HEIGHT_MM; // ~1.586

  // Snug-fit offset: our rings run tight, so add a small offset (mm)
  var SNUG_OFFSET_MM = 2.0;

  // Width-to-circumference multiplier (calibrated from 5 real scans)
  var WIDTH_TO_CIRC = 4.05;

  // Women's finger statistics (raw measured sizes, before +1 smart ring adjustment)
  // Used for sanity checking and average-based centering
  var FINGER_STATS = {
    Index:  { avgCirc: 53.5, avgSize: 6.75, minCirc: 47.8, maxCirc: 60.8, minSize: 4.5, maxSize: 9.5 },
    Middle: { avgCirc: 54.7, avgSize: 7.25, minCirc: 49.3, maxCirc: 62.1, minSize: 5,   maxSize: 10 },
    Ring:   { avgCirc: 52.2, avgSize: 6.25, minCirc: 46.8, maxCirc: 59.5, minSize: 4,   maxSize: 9 }
  };

  // US ring size table with half sizes (inner circumference in mm)
  var SIZE_TABLE = [
    { size: 3,    circ: 44.2 },
    { size: 3.5,  circ: 45.5 },
    { size: 4,    circ: 46.8 },
    { size: 4.5,  circ: 48.0 },
    { size: 5,    circ: 49.3 },
    { size: 5.5,  circ: 50.6 },
    { size: 6,    circ: 51.9 },
    { size: 6.5,  circ: 53.1 },
    { size: 7,    circ: 54.4 },
    { size: 7.5,  circ: 55.7 },
    { size: 8,    circ: 57.0 },
    { size: 8.5,  circ: 58.3 },
    { size: 9,    circ: 59.5 },
    { size: 9.5,  circ: 60.8 },
    { size: 10,   circ: 62.1 },
    { size: 10.5, circ: 63.4 },
    { size: 11,   circ: 64.6 },
    { size: 11.5, circ: 65.9 },
    { size: 12,   circ: 67.2 },
    { size: 12.5, circ: 68.5 },
    { size: 13,   circ: 69.7 },
    { size: 13.5, circ: 71.0 },
    { size: 14,   circ: 72.3 }
  ];

  // Finger definitions: name + MediaPipe landmark indices (no pinky — not a ring finger)
  var FINGERS = [
    { name: 'Index',  mcp: 5,  pip: 6,  adjacentMcp: 9  },
    { name: 'Middle', mcp: 9,  pip: 10, adjacentMcp: 13 },
    { name: 'Ring',   mcp: 13, pip: 14, adjacentMcp: 17 }
  ];

  var MEDIAPIPE_VISION_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/vision_bundle.mjs';
  var MEDIAPIPE_WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';
  var HAND_MODEL_URL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task';

  // Supabase config
  var SUPABASE_URL = 'https://jxnavujsqrmdjpdtozzx.supabase.co';
  var SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp4bmF2dWpzcXJtZGpwZHRvenp4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ4MzgzMDQsImV4cCI6MjA5MDQxNDMwNH0.Tlwl6cUBTM4QK8d89VTP9DmF-Pv5IHE7l5enNacqIP0';
  var supabaseClient = null;
  var calibrationOffsets = []; // fetched from Supabase

  /* ============================================================
     STATE
     ============================================================ */
  var overlay, video, captureCanvas, captureCtx;
  var handLandmarker = null;
  var stream = null;
  var detectionLoopId = null;
  var handDetected = false;
  var mediaPipeReady = false;
  var results = null;
  var calibPoints = [];
  var capturedImageData = null;
  var capturedLandmarks = null;

  // Device orientation state
  var orientationGranted = false;
  var currentOrientation = { alpha: 0, beta: 90, gamma: 0 };
  var capturedOrientation = null;

  /* ============================================================
     INITIALIZATION
     ============================================================ */
  function init() {
    overlay = document.getElementById('ringSizerOverlay');
    if (!overlay) return;

    captureCanvas = document.getElementById('sizerCanvas');
    captureCtx = captureCanvas ? captureCanvas.getContext('2d') : null;
    video = document.getElementById('sizerVideo');

    // Bind all action buttons via data-action
    overlay.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.dataset.action;

      switch (action) {
        case 'close': closeSizer(); break;
        case 'goto': showStep(btn.dataset.target); break;
        case 'request-orientation': showMotionModal(btn); break;
        case 'confirm-motion-permission': confirmMotionPermission(); break;
        case 'close-motion-modal': closeMotionModal(); break;
        case 'start-camera': onStartCamera(); break;
        case 'stop-camera': stopCamera(); showStep('instructions'); break;
        case 'capture': onCapture(); break;
        case 'select-size': onSelectSize(); break;
        case 'retake': onRetake(); break;
      }
    });

    // Bind trigger buttons
    document.addEventListener('click', function (e) {
      if (e.target.closest('.ring-sizer-trigger')) {
        e.preventDefault();
        openSizer();
      }
    });

    // Manual calibration canvas — touch + click with drag support
    var calibCanvas = document.getElementById('sizerCalibCanvas');
    if (calibCanvas) {
      var lastTouchTime = 0;
      var isTouchDevice = false;

      function getCalibXY(touch, canvas) {
        var rect = canvas.getBoundingClientRect();
        return {
          x: (touch.clientX - rect.left) * (canvas.width / rect.width),
          y: (touch.clientY - rect.top) * (canvas.height / rect.height)
        };
      }

      calibCanvas.addEventListener('touchstart', function (e) {
        e.preventDefault();
        e.stopPropagation();
        isTouchDevice = true;
        lastTouchTime = Date.now();
        var pos = getCalibXY(e.touches[0], calibCanvas);
        onCalibTapAt(pos.x, pos.y);
      }, { passive: false, capture: true });

      calibCanvas.addEventListener('touchmove', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var pos = getCalibXY(e.touches[0], calibCanvas);
        onCalibDrag(pos.x, pos.y);
      }, { passive: false, capture: true });

      calibCanvas.addEventListener('touchend', function (e) {
        e.preventDefault();
        e.stopPropagation();
        onCalibDragEnd();
      }, { capture: true });

      // Block ALL click events on touch devices — they're always synthetic
      // Only allow click on desktop (mouse) where no touch events fire
      calibCanvas.addEventListener('click', function (e) {
        if (isTouchDevice || Date.now() - lastTouchTime < 1000) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onCalibTap(e);
      }, { capture: true });

      // Also block mousedown/mouseup on touch devices (some in-app browsers fire these)
      calibCanvas.addEventListener('mousedown', function (e) {
        if (isTouchDevice) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true });
      calibCanvas.addEventListener('mouseup', function (e) {
        if (isTouchDevice) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true });
    }

    // Load Supabase and fetch calibration offsets
    loadSupabase();
  }

  /* ============================================================
     SUPABASE INTEGRATION
     ============================================================ */
  function loadSupabase() {
    var script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
    script.onload = function () {
      if (window.supabase && window.supabase.createClient) {
        supabaseClient = window.supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
        console.log('[RingSizer] Supabase connected');
        fetchCalibrationOffsets();
      }
    };
    script.onerror = function () {
      console.warn('[RingSizer] Supabase CDN failed to load - running without telemetry');
    };
    document.head.appendChild(script);
  }

  function fetchCalibrationOffsets() {
    if (!supabaseClient) return;
    supabaseClient
      .from('calibration_offsets')
      .select('finger, size_range_min, size_range_max, offset_mm')
      .eq('is_active', true)
      .then(function (res) {
        if (res.data && res.data.length > 0) {
          calibrationOffsets = res.data;
          console.log('[RingSizer] Loaded', calibrationOffsets.length, 'calibration offsets');
        } else {
          console.log('[RingSizer] No active calibration offsets - using base algorithm');
        }
      })
      .catch(function () {
        console.warn('[RingSizer] Failed to fetch calibration offsets');
      });
  }

  function getCalibrationOffset(fingerName, rawCircMm) {
    for (var i = 0; i < calibrationOffsets.length; i++) {
      var o = calibrationOffsets[i];
      if (o.finger === fingerName && rawCircMm >= o.size_range_min && rawCircMm <= o.size_range_max) {
        return o.offset_mm;
      }
    }
    return 0;
  }

  function saveSessionToSupabase(sessionId, measureResults) {
    if (!supabaseClient) return;
    var fingers = measureResults.fingers;
    var indexF = null, middleF = null, ringF = null;
    for (var i = 0; i < fingers.length; i++) {
      if (fingers[i].name === 'Index') indexF = fingers[i];
      if (fingers[i].name === 'Middle') middleF = fingers[i];
      if (fingers[i].name === 'Ring') ringF = fingers[i];
    }

    var cd = window._calibDebug || {};
    var row = {
      session_id: sessionId,
      device_model: navigator.userAgent,
      screen_width: window.screen ? window.screen.width : null,
      screen_height: window.screen ? window.screen.height : null,
      orientation_beta: capturedOrientation ? capturedOrientation.beta : null,
      orientation_gamma: capturedOrientation ? capturedOrientation.gamma : null,
      reticle_was_green: true,
      finger_index_mm: indexF ? indexF.adjustedCirc : null,
      finger_middle_mm: middleF ? middleF.adjustedCirc : null,
      finger_ring_mm: ringF ? ringF.adjustedCirc : null,
      finger_pinky_mm: null,
      rec_size_index: indexF ? indexF.size : null,
      rec_size_middle: middleF ? middleF.size : null,
      rec_size_ring: ringF ? ringF.size : null,
      rec_size_pinky: null,
      card_detected: measureResults.calibrationMethod === 'manual-card',
      calibration_version: 1,
      // Debug measurement data
      raw_pixel_width_index: indexF ? indexF.widthPx : null,
      raw_pixel_width_middle: middleF ? middleF.widthPx : null,
      raw_pixel_width_ring: ringF ? ringF.widthPx : null,
      raw_pixel_width_pinky: null,
      card_pixel_width: cd.topW ? parseFloat(cd.topW) : null,
      card_pixel_height: cd.leftH ? parseFloat(cd.leftH) : null,
      card_aspect_ratio: cd.topW && cd.leftH ? (parseFloat(cd.topW) / parseFloat(cd.leftH)) : null,
      pixels_to_mm_ratio: measureResults.pxPerMm || null,
      raw_width_mm_index: indexF ? indexF.widthMm : null,
      raw_width_mm_middle: middleF ? middleF.widthMm : null,
      raw_width_mm_ring: ringF ? ringF.widthMm : null,
      raw_width_mm_pinky: null,
      circumference_mm_index: indexF ? indexF.circumference : null,
      circumference_mm_middle: middleF ? middleF.circumference : null,
      circumference_mm_ring: ringF ? ringF.circumference : null,
      circumference_mm_pinky: null,
      raw_size_before_adjustment_index: indexF ? indexF.trueSize : null,
      raw_size_before_adjustment_middle: middleF ? middleF.trueSize : null,
      raw_size_before_adjustment_ring: ringF ? ringF.trueSize : null,
      raw_size_before_adjustment_pinky: null,
      camera_correction_factor: 1.42,
      landmark_pairs_used: {
        index: { mcp: 5, pip: 6, adjMcp: 9, colorPx: indexF ? indexF.colorPx : null, landmarkPx: indexF ? indexF.landmarkPx : null },
        middle: { mcp: 9, pip: 10, adjMcp: 13, colorPx: middleF ? middleF.colorPx : null, landmarkPx: middleF ? middleF.landmarkPx : null },
        ring: { mcp: 13, pip: 14, adjMcp: 17, colorPx: ringF ? ringF.colorPx : null, landmarkPx: ringF ? ringF.landmarkPx : null }
      }
    };

    supabaseClient
      .from('sizing_sessions')
      .insert([row])
      .then(function (res) {
        if (res.error) {
          console.warn('[RingSizer] Supabase insert error:', res.error.message);
        } else {
          console.log('[RingSizer] Session saved:', sessionId);
        }
      })
      .catch(function (err) {
        console.warn('[RingSizer] Supabase insert failed:', err);
      });
  }

  /* ============================================================
     DEVICE ORIENTATION
     ============================================================ */
  /* Pre-permission modal (Aitaca-style) */
  var _orientBtn = null;
  var _pendingCameraStart = false;

  function showMotionModal(btn) {
    _orientBtn = btn;
    _pendingCameraStart = false;
    // On Android / non-iOS, skip the modal — no permission needed
    if (typeof DeviceOrientationEvent === 'undefined' ||
        typeof DeviceOrientationEvent.requestPermission !== 'function') {
      requestOrientationPermission(btn);
      return;
    }
    var modal = document.getElementById('sizerMotionModal');
    if (modal) modal.style.display = 'flex';
  }

  function confirmMotionPermission() {
    closeMotionModal(true);
    // Request the actual iOS permission
    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then(function (state) {
          if (state === 'granted') {
            orientationGranted = true;
            startOrientationListener();
            if (_orientBtn) updateOrientationButton(_orientBtn, true);
          } else {
            if (_orientBtn) updateOrientationButton(_orientBtn, false, 'Permission denied');
          }
          // Resume camera start if this was triggered from Start Camera
          if (_pendingCameraStart) {
            _pendingCameraStart = false;
            onStartCamera();
          }
        })
        .catch(function () {
          if (_orientBtn) updateOrientationButton(_orientBtn, false, 'Not available');
          if (_pendingCameraStart) {
            _pendingCameraStart = false;
            onStartCamera();
          }
        });
    } else {
      requestOrientationPermission(_orientBtn);
      if (_pendingCameraStart) {
        _pendingCameraStart = false;
        onStartCamera();
      }
    }
  }

  function closeMotionModal(skipResume) {
    var modal = document.getElementById('sizerMotionModal');
    if (modal) modal.style.display = 'none';
    // If they dismiss modal without granting, still start camera
    if (!skipResume && _pendingCameraStart) {
      _pendingCameraStart = false;
      orientationGranted = false; // they skipped it
      onStartCamera();
    }
  }

  function requestOrientationPermission(btn) {
    // iOS 13+ requires explicit permission request from user gesture
    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then(function (state) {
          if (state === 'granted') {
            orientationGranted = true;
            startOrientationListener();
            updateOrientationButton(btn, true);
          } else {
            updateOrientationButton(btn, false, 'Permission denied');
          }
        })
        .catch(function () {
          updateOrientationButton(btn, false, 'Not available');
        });
    } else if ('DeviceOrientationEvent' in window) {
      // Android / non-iOS — no permission needed
      orientationGranted = true;
      startOrientationListener();
      updateOrientationButton(btn, true);
    } else {
      updateOrientationButton(btn, false, 'Not supported');
    }
  }

  function startOrientationListener() {
    window.addEventListener('deviceorientation', function (e) {
      currentOrientation = {
        alpha: e.alpha || 0,
        beta: e.beta || 0,
        gamma: e.gamma || 0
      };
      updateAngleWarning();
    });
  }

  function updateOrientationButton(btn, success, msg) {
    if (success) {
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg> Precise measurement enabled';
      btn.classList.add('sizer__orient-btn--granted');
      btn.disabled = true;
    } else {
      btn.innerHTML = msg || 'Not available';
      btn.classList.add('sizer__orient-btn--denied');
      btn.disabled = true;
    }
  }

  var isPhoneLevel = false;
  var levelState = 'neutral';

  function updateAngleWarning() {
    var ring = document.getElementById('sizerReticleRing');
    var dot = document.getElementById('sizerReticleDot');

    // If orientation not available, treat as level (bypass leveling)
    if (!orientationGranted) {
      isPhoneLevel = true;
      levelState = 'green';
      if (ring) ring.setAttribute('stroke', '#4CAF50');
      if (dot) {
        dot.setAttribute('fill', '#4CAF50');
        dot.setAttribute('cx', '80');
        dot.setAttribute('cy', '80');
      }
      return;
    }

    var beta = currentOrientation.beta;
    var gamma = currentOrientation.gamma;
    // Phone flat (facing down at table): beta ≈ 0, gamma ≈ 0
    var betaOff = beta;
    var gammaOff = gamma;

    // Move dot proportionally to tilt — gentle scaling for easy alignment
    var maxOff = 60;
    var scale = 1.2;
    var dx = Math.max(-maxOff, Math.min(maxOff, gammaOff * scale));
    var dy = Math.max(-maxOff, Math.min(maxOff, -betaOff * scale));

    // Distance from center determines color state (generous green zone)
    var dist = Math.sqrt(dx * dx + dy * dy);
    var state;
    if (dist <= 15) {
      state = 'green';
      isPhoneLevel = true;
    } else if (dist <= 35) {
      state = 'orange';
      isPhoneLevel = false;
    } else {
      state = 'red';
      isPhoneLevel = false;
    }
    levelState = state;

    // Update SVG attributes (works regardless of CSS)
    var colors = { green: '#4CAF50', orange: '#FF9800', red: '#EF5350' };
    var c = colors[state] || '#EF5350';
    if (ring) ring.setAttribute('stroke', c);
    if (dot) {
      dot.setAttribute('fill', c);
      dot.setAttribute('cx', String(80 + dx));
      dot.setAttribute('cy', String(80 + dy));
    }
  }

  function getPerspectiveCorrectionFactor() {
    if (!capturedOrientation || !orientationGranted) return 1;

    var beta = capturedOrientation.beta;
    var gamma = capturedOrientation.gamma;

    // Android devices sometimes report β=90 when orientation is broken/unsupported
    // In this case, skip perspective correction entirely (return neutral)
    if (beta === 90 && gamma === 0) return 1;

    // Phone is flat facing down: beta ≈ 0, gamma ≈ 0
    var betaOff = Math.abs(beta);
    var gammaOff = Math.abs(gamma);

    // Convert to radians
    var tiltRad = Math.sqrt(betaOff * betaOff + gammaOff * gammaOff) * (Math.PI / 180);

    // At angle θ from vertical, objects appear foreshortened by cos(θ)
    // The measured width in pixels is the projected width, so true width = measured / cos(θ)
    // Clamp to avoid extreme corrections (max 30° = ~1.15x correction)
    if (tiltRad > 0.524) tiltRad = 0.524; // cap at 30°
    return 1 / Math.cos(tiltRad);
  }

  /* ============================================================
     OPEN / CLOSE
     ============================================================ */
  function openSizer() {
    if (!overlay) overlay = document.getElementById('ringSizerOverlay');
    if (!overlay) return;

    // Move to body to escape any parent transform/stacking context
    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    // Check if mobile (has camera)
    var isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 0 && window.innerWidth < 1024);

    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (isMobile) {
      showStep('method');
    } else {
      // Desktop fallback
      var urlBox = document.getElementById('sizerUrlBox');
      if (urlBox) urlBox.textContent = window.location.origin + '/pages/ring-sizer';
      showStep('desktop');
    }
  }

  function closeSizer() {
    stopCamera();
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    hideAllSteps();
  }

  /* ============================================================
     STEP NAVIGATION
     ============================================================ */
  function showStep(name) {
    hideAllSteps();
    var step = overlay.querySelector('[data-step="' + name + '"]');
    if (step) step.classList.add('active');
  }

  function hideAllSteps() {
    var steps = overlay.querySelectorAll('.sizer__step');
    for (var i = 0; i < steps.length; i++) steps[i].classList.remove('active');
  }

  /* ============================================================
     MEDIAPIPE LOADING
     ============================================================ */
  async function loadMediaPipe() {
    if (mediaPipeReady) return;

    var loadingEl = document.getElementById('sizerLoading');
    if (loadingEl) loadingEl.classList.add('active');

    try {
      var mod = await import(MEDIAPIPE_VISION_URL);
      var FilesetResolver = mod.FilesetResolver;
      var HandLandmarker = mod.HandLandmarker;

      var vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);

      handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: HAND_MODEL_URL,
          delegate: 'GPU'
        },
        numHands: 1,
        runningMode: 'VIDEO'
      });

      mediaPipeReady = true;
    } catch (err) {
      console.error('MediaPipe load failed:', err);
      showError('Failed to Load', 'Unable to load the AI model. Please check your internet connection and try again.');
      throw err;
    } finally {
      if (loadingEl) loadingEl.classList.remove('active');
    }
  }

  /* ============================================================
     CAMERA
     ============================================================ */
  function injectCameraOverlays() {
    var wrap = document.querySelector('.sizer__camera-wrap');
    if (!wrap) return;
    var svgNS = 'http://www.w3.org/2000/svg';

    // Remove old injected overlays (prevent duplicates)
    var old = wrap.querySelectorAll('[data-injected]');
    old.forEach(function(el) { el.remove(); });

    // --- 1. DIVIDER at 25% ---
    var divider = document.createElement('div');
    divider.setAttribute('data-injected', 'divider');
    divider.style.position = 'absolute';
    divider.style.left = '0';
    divider.style.right = '0';
    divider.style.top = '25%';
    divider.style.display = 'flex';
    divider.style.flexDirection = 'column';
    divider.style.alignItems = 'center';
    divider.style.pointerEvents = 'none';
    divider.style.zIndex = '50';

    var cardLabel = document.createElement('div');
    cardLabel.textContent = '\u2191 Card \u2191';
    cardLabel.style.cssText = 'font-size:13px;font-weight:700;color:#fff;letter-spacing:0.04em;margin-bottom:6px;text-shadow:0 1px 4px rgba(0,0,0,0.6);';
    divider.appendChild(cardLabel);

    // Divider line via SVG (CSS backgrounds don't render in this context)
    var lineSvg = document.createElementNS(svgNS, 'svg');
    lineSvg.setAttribute('width', '100%');
    lineSvg.setAttribute('height', '3');
    lineSvg.style.cssText = 'display:block;';
    var svgLine = document.createElementNS(svgNS, 'rect');
    svgLine.setAttribute('width', '100%');
    svgLine.setAttribute('height', '3');
    svgLine.setAttribute('fill', '#ffffff');
    lineSvg.appendChild(svgLine);
    divider.appendChild(lineSvg);

    var handLabel = document.createElement('div');
    handLabel.textContent = '\u2193 Hand \u2193';
    handLabel.style.cssText = 'font-size:13px;font-weight:700;color:#fff;letter-spacing:0.04em;margin-top:6px;text-shadow:0 1px 4px rgba(0,0,0,0.6);';
    divider.appendChild(handLabel);

    wrap.appendChild(divider);

    // --- 2. RETICLE — centered in hand zone (~58% from top) ---
    var reticleContainer = document.createElement('div');
    reticleContainer.setAttribute('data-injected', 'reticle');
    reticleContainer.style.position = 'absolute';
    reticleContainer.style.top = '58%';
    reticleContainer.style.left = '50%';
    reticleContainer.style.pointerEvents = 'none';
    reticleContainer.style.zIndex = '50';
    reticleContainer.style.transform = 'translate(-50%, -50%)';

    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', '160');
    svg.setAttribute('height', '160');
    svg.setAttribute('viewBox', '0 0 160 160');
    svg.id = 'sizerReticle';
    svg.style.cssText = 'overflow:visible;display:block;';

    // Outer ring (static)
    var outerCircle = document.createElementNS(svgNS, 'circle');
    outerCircle.setAttribute('cx', '80');
    outerCircle.setAttribute('cy', '80');
    outerCircle.setAttribute('r', '77');
    outerCircle.setAttribute('fill', 'none');
    outerCircle.setAttribute('stroke', '#EF5350');
    outerCircle.setAttribute('stroke-width', '3');
    outerCircle.id = 'sizerReticleRing';
    svg.appendChild(outerCircle);

    // Target circle (static, center)
    var targetCircle = document.createElementNS(svgNS, 'circle');
    targetCircle.setAttribute('cx', '80');
    targetCircle.setAttribute('cy', '80');
    targetCircle.setAttribute('r', '12');
    targetCircle.setAttribute('fill', 'none');
    targetCircle.setAttribute('stroke', 'rgba(255,255,255,0.9)');
    targetCircle.setAttribute('stroke-width', '2.5');
    svg.appendChild(targetCircle);

    // Moving dot (responds to gyroscope)
    var dotCircle = document.createElementNS(svgNS, 'circle');
    dotCircle.setAttribute('cx', '80');
    dotCircle.setAttribute('cy', '80');
    dotCircle.setAttribute('r', '9');
    dotCircle.setAttribute('fill', '#EF5350');
    dotCircle.id = 'sizerReticleDot';
    svg.appendChild(dotCircle);

    reticleContainer.appendChild(svg);
    wrap.appendChild(reticleContainer);
  }

  async function onStartCamera() {
    try {
      // If orientation not granted yet on iOS, show the permission modal first
      if (!orientationGranted &&
          typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        // Show motion modal; after granting, it will proceed to camera
        _pendingCameraStart = true;
        var modal = document.getElementById('sizerMotionModal');
        if (modal) { modal.style.display = 'flex'; return; }
      }

      // Load MediaPipe first (shows loading overlay)
      await loadMediaPipe();

      // Request camera
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      video.srcObject = stream;
      await video.play();

      // Try to enable torch/flash for better lighting
      try {
        var track = stream.getVideoTracks()[0];
        var capabilities = track.getCapabilities ? track.getCapabilities() : {};
        if (capabilities.torch) {
          await track.applyConstraints({ advanced: [{ torch: true }] });
          console.log('[RingSizer] Torch enabled');
        }
      } catch (torchErr) {
        console.log('[RingSizer] Torch not available:', torchErr.message);
      }

      showStep('camera');
      injectCameraOverlays();
      startDetectionLoop();
    } catch (err) {
      console.error('Camera error:', err);
      // Delay error display to prevent flash during normal loading
      await delay(500);
      // Only show error if we're not already on camera step (meaning it actually failed)
      var currentStep = overlay.querySelector('.sizer__step.active');
      if (currentStep && currentStep.dataset.step !== 'camera') {
        if (err.name === 'NotAllowedError') {
          showError('Camera Access Denied', 'Please allow camera access in your browser settings to use the ring sizer.');
        } else {
          showError('Camera Error', 'Unable to access your camera. Please try again.');
        }
      }
    }
  }

  function stopCamera() {
    if (detectionLoopId) {
      cancelAnimationFrame(detectionLoopId);
      detectionLoopId = null;
    }
    if (stream) {
      stream.getTracks().forEach(function (t) { t.stop(); });
      stream = null;
    }
    if (video) {
      video.srcObject = null;
    }
    // Clear tracking canvas
    var tc = document.getElementById('sizerTrackingCanvas');
    if (tc) {
      var ctx = tc.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, tc.width, tc.height);
    }
  }

  /* ============================================================
     REAL-TIME HAND DETECTION LOOP — draws green circle on palm
     ============================================================ */
  function startDetectionLoop() {
    var captureBtn = document.getElementById('sizerCaptureBtn');
    var trackingCanvas = document.getElementById('sizerTrackingCanvas');
    var trackingCtx = trackingCanvas ? trackingCanvas.getContext('2d') : null;
    var lastTime = -1;
    var frameCount = 0;
    var DETECT_EVERY = 3; // run detection every N frames for performance
    var lastLandmarks = null;

    function syncCanvasSize() {
      if (!trackingCanvas || !video) return;
      if (trackingCanvas.width !== video.videoWidth || trackingCanvas.height !== video.videoHeight) {
        trackingCanvas.width = video.videoWidth;
        trackingCanvas.height = video.videoHeight;
      }
    }

    // Hand detection runs silently — no visual tracking on canvas
    function clearTrackingCanvas() {
      if (!trackingCtx || !trackingCanvas) return;
      trackingCtx.clearRect(0, 0, trackingCanvas.width, trackingCanvas.height);
    }

    function loop() {
      if (!handLandmarker || !video || video.paused || video.readyState < 2) {
        detectionLoopId = requestAnimationFrame(loop);
        return;
      }

      var now = performance.now();
      if (now === lastTime) {
        detectionLoopId = requestAnimationFrame(loop);
        return;
      }
      lastTime = now;
      frameCount++;

      // Only run detection every N frames
      if (frameCount % DETECT_EVERY === 0) {
        try {
          var result = handLandmarker.detectForVideo(video, now);
          handDetected = result.landmarks && result.landmarks.length > 0;
          lastLandmarks = handDetected ? result.landmarks : null;
        } catch (e) {
          handDetected = false;
          lastLandmarks = null;
        }
      }

      // Clear canvas (hand detection is silent — no visual on canvas)
      syncCanvasSize();
      clearTrackingCanvas();

      // Update leveling reticle every frame
      updateAngleWarning();

      // Check for card in the upper portion of the frame
      var cardVisible = false;
      if (handDetected && frameCount % (DETECT_EVERY * 3) === 0) {
        try {
          // Sample the top 25% of the frame for edge/contrast variation
          // A card creates strong edges vs a plain surface
          var vw = video.videoWidth;
          var vh = video.videoHeight;
          var sampleCanvas = document.createElement('canvas');
          sampleCanvas.width = 80;
          sampleCanvas.height = 20;
          var sCtx = sampleCanvas.getContext('2d');
          // Sample the card zone (top 25%)
          sCtx.drawImage(video, 0, 0, vw, vh * 0.25, 0, 0, 80, 20);
          var sData = sCtx.getImageData(0, 0, 80, 20).data;
          // Calculate edge strength — sum of absolute differences between adjacent pixels
          var edgeSum = 0;
          for (var ei = 4; ei < sData.length; ei += 4) {
            edgeSum += Math.abs(sData[ei] - sData[ei - 4]);
            edgeSum += Math.abs(sData[ei + 1] - sData[ei - 3]);
            edgeSum += Math.abs(sData[ei + 2] - sData[ei - 2]);
          }
          var avgEdge = edgeSum / (sData.length / 4);
          // A card on a surface creates visible edges (threshold ~3-5)
          cardVisible = avgEdge > 3.5;
        } catch (cardErr) {
          cardVisible = true; // assume card present if detection fails
        }
      } else if (handDetected) {
        // Between checks, keep previous state
        cardVisible = captureBtn && !captureBtn.disabled;
      }

      // Single status bar — priority: no hand > no card > tilt warning > ready
      var statusBar = document.getElementById('sizerStatusBar');
      var ready = handDetected && cardVisible && isPhoneLevel;

      if (captureBtn) {
        captureBtn.disabled = !ready;
        captureBtn.style.opacity = ready ? '1' : '0.35';
      }

      if (statusBar) {
        if (!handDetected) {
          statusBar.textContent = 'Place your hand and card on a flat surface';
          statusBar.className = 'sizer__status-bar sizer__status-bar--neutral';
        } else if (!cardVisible) {
          statusBar.textContent = 'Place a card above the line so it\u0027s fully visible';
          statusBar.className = 'sizer__status-bar sizer__status-bar--orange';
        } else if (orientationGranted && levelState === 'red') {
          statusBar.textContent = 'Hold phone flat \u2014 align the dot to the center circle';
          statusBar.className = 'sizer__status-bar sizer__status-bar--red';
        } else if (orientationGranted && levelState === 'orange') {
          statusBar.textContent = 'Almost level \u2014 align the dot to the center circle';
          statusBar.className = 'sizer__status-bar sizer__status-bar--orange';
        } else {
          statusBar.textContent = 'Card detected \u2014 take the photo now';
          statusBar.className = 'sizer__status-bar sizer__status-bar--green';
        }
      }

      detectionLoopId = requestAnimationFrame(loop);
    }

    loop();
  }

  /* ============================================================
     CAPTURE & ANALYSIS
     ============================================================ */
  async function onCapture() {
    if (!video || !captureCanvas) return;

    // Freeze device orientation at capture moment
    capturedOrientation = { alpha: currentOrientation.alpha, beta: currentOrientation.beta, gamma: currentOrientation.gamma };

    // === MULTI-FRAME CAPTURE ===
    // Capture 8 frames quickly for landmark averaging
    var FRAME_COUNT = 8;
    var FRAME_DELAY = 80; // ms between frames (~0.6s total capture)
    var multiFrameLandmarks = [];

    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;

    for (var fi = 0; fi < FRAME_COUNT; fi++) {
      captureCtx.drawImage(video, 0, 0);
      try {
        var now = performance.now();
        var result = handLandmarker.detectForVideo(video, now);
        if (result.landmarks && result.landmarks.length > 0) {
          multiFrameLandmarks.push(result.landmarks[0]);
        }
      } catch (e) { /* skip failed frame */ }
      if (fi < FRAME_COUNT - 1) await delay(FRAME_DELAY);
    }

    // Final frame stays on canvas for card detection + pixel analysis
    captureCtx.drawImage(video, 0, 0);

    // Stop camera + detection loop
    stopCamera();

    // Show processing
    showStep('processing');
    resetProcessingSteps();

    await analyze(multiFrameLandmarks).catch(function(err) {
      console.error('Analysis error:', err);
      // Only show error if we're still on the processing step (not already on calibrate/results)
      var currentStep = overlay.querySelector('.sizer__step.active');
      if (currentStep && currentStep.dataset.step === 'processing') {
        showError('Analysis Failed', 'Something went wrong during measurement. Please try again.');
      }
    });
  }

  // Average multiple landmark arrays into one
  function averageLandmarks(landmarkArrays) {
    if (landmarkArrays.length === 0) return null;
    if (landmarkArrays.length === 1) return landmarkArrays[0];

    var numPoints = landmarkArrays[0].length;
    var averaged = [];

    for (var p = 0; p < numPoints; p++) {
      var sx = 0, sy = 0, sz = 0;
      for (var f = 0; f < landmarkArrays.length; f++) {
        sx += landmarkArrays[f][p].x;
        sy += landmarkArrays[f][p].y;
        sz += landmarkArrays[f][p].z;
      }
      averaged.push({
        x: sx / landmarkArrays.length,
        y: sy / landmarkArrays.length,
        z: sz / landmarkArrays.length
      });
    }

    return averaged;
  }

  async function analyze(multiFrameLandmarks) {
    // Step 1: Detect hand (use multi-frame averaged landmarks)
    updateProcessingStep(0);
    await delay(1500);

    var avgLandmarks;

    // Filter out any null/invalid entries from multi-frame
    var validFrames = (multiFrameLandmarks || []).filter(function(f) { return f && f.length > 0; });

    if (validFrames.length > 0) {
      avgLandmarks = averageLandmarks(validFrames);
      console.log('[RingSizer] Multi-frame: averaged', validFrames.length, 'frames');
    } else {
      // Fallback: try single frame detection, then retry once
      await handLandmarker.setOptions({ runningMode: 'IMAGE' });
      var result = handLandmarker.detect(captureCanvas);
      await handLandmarker.setOptions({ runningMode: 'VIDEO' });

      if (!result.landmarks || result.landmarks.length === 0) {
        // Retry once after a short delay
        await delay(500);
        await handLandmarker.setOptions({ runningMode: 'IMAGE' });
        result = handLandmarker.detect(captureCanvas);
        await handLandmarker.setOptions({ runningMode: 'VIDEO' });
      }

      if (!result.landmarks || result.landmarks.length === 0) {
        showError('Hand Not Detected', "We couldn't detect your hand clearly. Try again with better lighting and a plain background.");
        return;
      }
      avgLandmarks = result.landmarks[0];
      console.log('[RingSizer] Single frame fallback');
    }

    capturedLandmarks = avgLandmarks;
    completeProcessingStep(0);

    // Step 2: Always use manual 4-corner card calibration for accuracy
    // Auto detection was too unreliable — user tapping corners is more precise
    updateProcessingStep(1);
    await delay(800);
    completeProcessingStep(1);

    console.log('[RingSizer] Proceeding to manual 4-corner card calibration.');
    showManualCalibration();
  }

  /* ============================================================
     CARD DETECTION (Automatic)
     Edge-based detection in upper portion of image
     ============================================================ */
  function detectCard(canvas) {
    var w = canvas.width;
    var h = canvas.height;
    var ctx = canvas.getContext('2d');

    // Search entire image for card (card could be anywhere relative to hand)
    var regionH = h;
    var imgData = ctx.getImageData(0, 0, w, regionH);
    var data = imgData.data;
    var rw = w;
    var rh = regionH;

    // Convert to grayscale
    var gray = new Uint8Array(rw * rh);
    for (var i = 0; i < rw * rh; i++) {
      gray[i] = Math.round(0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]);
    }

    // Apply simple blur (3x3 box filter)
    var blurred = new Uint8Array(rw * rh);
    for (var y = 1; y < rh - 1; y++) {
      for (var x = 1; x < rw - 1; x++) {
        var sum = 0;
        for (var dy = -1; dy <= 1; dy++) {
          for (var dx = -1; dx <= 1; dx++) {
            sum += gray[(y + dy) * rw + (x + dx)];
          }
        }
        blurred[y * rw + x] = Math.round(sum / 9);
      }
    }

    // Compute gradient magnitudes (Sobel-like)
    var gradH = new Float32Array(rw * rh); // horizontal edges → vertical gradient
    var gradV = new Float32Array(rw * rh); // vertical edges → horizontal gradient

    for (var y = 1; y < rh - 1; y++) {
      for (var x = 1; x < rw - 1; x++) {
        gradH[y * rw + x] = Math.abs(blurred[(y + 1) * rw + x] - blurred[(y - 1) * rw + x]);
        gradV[y * rw + x] = Math.abs(blurred[y * rw + x + 1] - blurred[y * rw + x - 1]);
      }
    }

    // Project gradients: horizontal edges → row profile
    var rowProfile = new Float32Array(rh);
    for (var y = 0; y < rh; y++) {
      var s = 0;
      for (var x = Math.floor(rw * 0.1); x < Math.floor(rw * 0.9); x++) {
        s += gradH[y * rw + x];
      }
      rowProfile[y] = s;
    }

    // Project gradients: vertical edges → column profile
    var colProfile = new Float32Array(rw);
    for (var x = 0; x < rw; x++) {
      var s = 0;
      for (var y = Math.floor(rh * 0.05); y < Math.floor(rh * 0.95); y++) {
        s += gradV[y * rw + x];
      }
      colProfile[x] = s;
    }

    // Smooth profiles (moving average, window=5)
    rowProfile = smoothArray(rowProfile, 5);
    colProfile = smoothArray(colProfile, 5);

    // Find peaks in row profile (top and bottom edges of card)
    var minRowGap = Math.floor(rh * 0.1);
    var rowPeaks = findTopPeaks(rowProfile, 2, minRowGap);

    // Find peaks in column profile (left and right edges)
    var minColGap = Math.floor(rw * 0.1);
    var colPeaks = findTopPeaks(colProfile, 2, minColGap);

    if (rowPeaks.length < 2 || colPeaks.length < 2) {
      return { valid: false };
    }

    // Sort peaks
    rowPeaks.sort(function (a, b) { return a - b; });
    colPeaks.sort(function (a, b) { return a - b; });

    var cardTop = rowPeaks[0];
    var cardBottom = rowPeaks[1];
    var cardLeft = colPeaks[0];
    var cardRight = colPeaks[1];

    var cardWidthPx = cardRight - cardLeft;
    var cardHeightPx = cardBottom - cardTop;

    // Card must be at least 8% of image width and reasonable absolute size
    if (cardWidthPx < w * 0.08 || cardHeightPx < h * 0.04) return { valid: false };
    if (cardWidthPx < 50 || cardHeightPx < 30) return { valid: false };

    var aspect = cardWidthPx / cardHeightPx;

    // Check aspect ratio: should be close to 1.586 (credit card)
    // Allow some tolerance for perspective distortion: 1.30–1.85
    if (aspect > 1.30 && aspect < 1.85) {
      return { valid: true, widthPx: cardWidthPx, heightPx: cardHeightPx, aspect: aspect };
    }

    // Also try height-based if card is rotated
    if (1 / aspect > 1.40 && 1 / aspect < 1.75) {
      return { valid: true, widthPx: cardHeightPx, heightPx: cardWidthPx, aspect: 1 / aspect };
    }

    return { valid: false };
  }

  function smoothArray(arr, windowSize) {
    var result = new Float32Array(arr.length);
    var half = Math.floor(windowSize / 2);
    for (var i = 0; i < arr.length; i++) {
      var sum = 0, count = 0;
      for (var j = Math.max(0, i - half); j <= Math.min(arr.length - 1, i + half); j++) {
        sum += arr[j];
        count++;
      }
      result[i] = sum / count;
    }
    return result;
  }

  function findTopPeaks(arr, numPeaks, minGap) {
    var peaks = [];
    var threshold = 0;

    // Find max value for thresholding
    var maxVal = 0;
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] > maxVal) maxVal = arr[i];
    }
    threshold = maxVal * 0.3;

    // Find all local maxima above threshold
    var candidates = [];
    for (var i = 2; i < arr.length - 2; i++) {
      if (arr[i] > threshold && arr[i] >= arr[i - 1] && arr[i] >= arr[i + 1] &&
        arr[i] >= arr[i - 2] && arr[i] >= arr[i + 2]) {
        candidates.push({ idx: i, val: arr[i] });
      }
    }

    // Sort by value descending
    candidates.sort(function (a, b) { return b.val - a.val; });

    // Pick top peaks with minimum gap
    for (var i = 0; i < candidates.length && peaks.length < numPeaks; i++) {
      var tooClose = false;
      for (var j = 0; j < peaks.length; j++) {
        if (Math.abs(candidates[i].idx - peaks[j]) < minGap) {
          tooClose = true;
          break;
        }
      }
      if (!tooClose) peaks.push(candidates[i].idx);
    }

    return peaks;
  }

  /* ============================================================
     HAND-PROPORTION FALLBACK (no card detected)
     Uses average female hand width (~76mm) as reference
     ============================================================ */
  function estimateScaleFromHand(landmarks, imgW, imgH) {
    // Measure hand width across MCP joints (index MCP to pinky MCP)
    var idx5 = landmarks[5]; // Index MCP
    var idx17 = landmarks[17]; // Pinky MCP

    var dx = (idx5.x - idx17.x) * imgW;
    var dy = (idx5.y - idx17.y) * imgH;
    var handWidthPx = Math.sqrt(dx * dx + dy * dy);

    if (handWidthPx < 20) return 0;

    // Average female hand width across MCPs ≈ 68mm (women's sizing target)
    var AVG_HAND_WIDTH_MM = 68;
    return handWidthPx / AVG_HAND_WIDTH_MM;
  }

  /* ============================================================
     MANUAL CARD CALIBRATION
     User taps all 4 corners of their card for accurate calibration
     ============================================================ */
  var CORNER_LABELS = ['top-left', 'top-right', 'bottom-right', 'bottom-left'];

  function showManualCalibration() {
    calibPoints = [];
    draggingPointIdx = -1;
    isDragging = false;
    var btns = document.getElementById('sizerCalibBtns');
    if (btns) btns.style.display = 'none';
    showStep('calibrate');

    var calibCanvas = document.getElementById('sizerCalibCanvas');
    if (!calibCanvas) return;

    // Draw captured image on calibration canvas
    var maxW = Math.min(window.innerWidth - 32, 500);
    var maxH = window.innerHeight - 220;
    var scale = maxW / captureCanvas.width;
    if (captureCanvas.height * scale > maxH) {
      scale = maxH / captureCanvas.height;
    }
    calibCanvas.width = Math.round(captureCanvas.width * scale);
    calibCanvas.height = Math.round(captureCanvas.height * scale);
    calibCanvas.style.width = '';
    calibCanvas.style.height = '';

    var ctx = calibCanvas.getContext('2d');
    ctx.drawImage(captureCanvas, 0, 0, calibCanvas.width, calibCanvas.height);

    updateCalibText();
  }

  function onCalibTap(e) {
    var rect = e.target.getBoundingClientRect();
    var x = (e.clientX - rect.left) * (e.target.width / rect.width);
    var y = (e.clientY - rect.top) * (e.target.height / rect.height);
    onCalibTapAt(x, y);
  }

  var draggingPointIdx = -1;
  var isDragging = false;

  function onCalibTapAt(x, y) {
    var calibCanvas = document.getElementById('sizerCalibCanvas');
    if (!calibCanvas) return;

    // Check if tapping near an existing point to start dragging
    for (var i = 0; i < calibPoints.length; i++) {
      var dx = x - calibPoints[i].x;
      var dy = y - calibPoints[i].y;
      if (Math.sqrt(dx * dx + dy * dy) < 25) {
        draggingPointIdx = i;
        isDragging = true;
        return;
      }
    }

    if (calibPoints.length >= 4) return;
    calibPoints.push({ x: x, y: y });
    redrawCalibration();

    if (calibPoints.length < 4) {
      updateCalibText();
    } else {
      updateCalibText4Done();
    }
  }

  function onCalibDrag(x, y) {
    if (!isDragging || draggingPointIdx < 0) return;
    calibPoints[draggingPointIdx].x = x;
    calibPoints[draggingPointIdx].y = y;
    redrawCalibration();
  }

  function onCalibDragEnd() {
    isDragging = false;
    draggingPointIdx = -1;
  }

  function undoCalibPoint() {
    if (calibPoints.length === 0) return;
    calibPoints.pop();
    redrawCalibration();
    updateCalibText();
  }

  function confirmCalibration() {
    if (calibPoints.length < 4) return;
    try {
      finishManualCalibration();
    } catch (e) {
      console.error('[RingSizer] Calibration error:', e);
    }
  }

  function redrawCalibration() {
    var calibCanvas = document.getElementById('sizerCalibCanvas');
    if (!calibCanvas) return;
    var ctx = calibCanvas.getContext('2d');

    // Redraw captured image
    ctx.drawImage(captureCanvas, 0, 0, calibCanvas.width, calibCanvas.height);

    // Draw lines between points
    if (calibPoints.length > 1) {
      ctx.beginPath();
      ctx.moveTo(calibPoints[0].x, calibPoints[0].y);
      for (var i = 1; i < calibPoints.length; i++) {
        ctx.lineTo(calibPoints[i].x, calibPoints[i].y);
      }
      if (calibPoints.length === 4) {
        ctx.lineTo(calibPoints[0].x, calibPoints[0].y);
      }
      ctx.strokeStyle = '#2a72de';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw numbered dots
    for (var i = 0; i < calibPoints.length; i++) {
      var p = calibPoints[i];
      ctx.beginPath();
      ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = (isDragging && draggingPointIdx === i) ? '#fff' : '#2a72de';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = (isDragging && draggingPointIdx === i) ? '#2a72de' : '#fff';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(i + 1), p.x, p.y);
    }
  }

  function updateCalibText4Done() {
    var text = document.getElementById('sizerCalibText');
    var confirmBtn = document.getElementById('sizerCalibConfirmBtn');
    if (text) {
      text.innerHTML = '<strong>All 4 corners placed!</strong> Drag to adjust, then tap Confirm.';
    }
    if (confirmBtn) { confirmBtn.style.opacity = '1'; confirmBtn.style.pointerEvents = 'auto'; }
  }

  function updateCalibText() {
    var text = document.getElementById('sizerCalibText');
    var confirmBtn = document.getElementById('sizerCalibConfirmBtn');
    if (!text) return;
    var n = calibPoints.length;
    if (n < 4) {
      text.innerHTML = 'Tap the <strong>' + CORNER_LABELS[n] + ' corner</strong> of your card (' + (n + 1) + ' of 4)';
      if (confirmBtn) { confirmBtn.style.opacity = '0.3'; confirmBtn.style.pointerEvents = 'none'; }
    }
  }

  async function finishManualCalibration() {
    // Points: 0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left
    // All coordinates are in calibration canvas pixel space
    // We need pxPerMm in CAPTURE canvas space (where landmarks + image data live)

    var calibCanvasEl = document.getElementById('sizerCalibCanvas');
    var calibToCapture = captureCanvas.width / calibCanvasEl.width;

    // Measure card dimensions in calibration canvas pixels
    var dx1 = calibPoints[1].x - calibPoints[0].x;
    var dy1 = calibPoints[1].y - calibPoints[0].y;
    var topWidthCalib = Math.sqrt(dx1 * dx1 + dy1 * dy1);

    var dx2 = calibPoints[2].x - calibPoints[3].x;
    var dy2 = calibPoints[2].y - calibPoints[3].y;
    var bottomWidthCalib = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    var dx3 = calibPoints[3].x - calibPoints[0].x;
    var dy3 = calibPoints[3].y - calibPoints[0].y;
    var leftHeightCalib = Math.sqrt(dx3 * dx3 + dy3 * dy3);

    var dx4 = calibPoints[2].x - calibPoints[1].x;
    var dy4 = calibPoints[2].y - calibPoints[1].y;
    var rightHeightCalib = Math.sqrt(dx4 * dx4 + dy4 * dy4);

    // Validate card aspect ratio — should be ~1.586 (85.6/53.98)
    var avgWidthCalib = (topWidthCalib + bottomWidthCalib) / 2;
    var avgHeightCalib = (leftHeightCalib + rightHeightCalib) / 2;
    var detectedRatio = avgWidthCalib / avgHeightCalib;
    var expectedRatio = CARD_WIDTH_MM / CARD_HEIGHT_MM; // 1.586
    var ratioError = Math.abs(detectedRatio - expectedRatio) / expectedRatio;

    if (ratioError > 0.25) {
      // Ratio is way off — corners were probably tapped wrong
      calibPoints = [];
      redrawCalibration();
      updateCalibText();
      var confirmBtn = document.getElementById('sizerCalibConfirmBtn');
      if (confirmBtn) { confirmBtn.style.opacity = '0.3'; confirmBtn.style.pointerEvents = 'none'; }
      var calibText = document.getElementById('sizerCalibText');
      if (calibText) calibText.innerHTML = '<strong style="color:#dc2626;">Card corners don\'t look right.</strong> Please tap the exact corners of your card again.';
      return;
    }

    // Use card WIDTH only for pxPerMm — width taps (left/right edges) are
    // more accurate than height taps (top/bottom). Height is validated via
    // aspect ratio but not used for the primary calibration.
    var calibPxPerMm = avgWidthCalib / CARD_WIDTH_MM;

    // Scale to capture canvas space
    var pxPerMm = calibPxPerMm * calibToCapture;

    console.log('[RingSizer] Calibration debug:',
      'calibCanvas:', calibCanvasEl.width + 'x' + calibCanvasEl.height,
      'captureCanvas:', captureCanvas.width + 'x' + captureCanvas.height,
      'calibToCapture:', calibToCapture.toFixed(2),
      'calibPxPerMm:', calibPxPerMm.toFixed(3),
      'final pxPerMm:', pxPerMm.toFixed(3));

    // Store debug info for display
    window._calibDebug = {
      calibW: calibCanvasEl.width,
      calibH: calibCanvasEl.height,
      captureW: captureCanvas.width,
      captureH: captureCanvas.height,
      calibToCapture: calibToCapture,
      topW: topWidthCalib.toFixed(1),
      botW: bottomWidthCalib.toFixed(1),
      leftH: leftHeightCalib.toFixed(1),
      rightH: rightHeightCalib.toFixed(1),
      calibPxPerMm: calibPxPerMm.toFixed(3),
      finalPxPerMm: pxPerMm.toFixed(3),
      points: calibPoints.map(function(p) { return Math.round(p.x) + ',' + Math.round(p.y); }).join(' | ')
    };

    // No clamping — let it through so we can debug
    if (pxPerMm < 0.5) pxPerMm = 0.5;

    // Continue with measurement
    showStep('processing');
    resetProcessingSteps();

    completeProcessingStep(0);
    updateProcessingStep(1);
    await delay(800);
    completeProcessingStep(1);

    updateProcessingStep(2);
    await delay(1200);
    results = measureAllFingers(capturedLandmarks, captureCanvas, pxPerMm, false);
    results.calibrationMethod = 'manual-card';
    results.pxPerMm = pxPerMm;
    results.canvasW = captureCanvas.width;
    results.canvasH = captureCanvas.height;
    completeProcessingStep(2);

    // Validate results
    var sizes = results.fingers.map(function(f) { return f.size; });
    var allCircs = results.fingers.map(function(f) { return f.adjustedCirc.toFixed(1); });

    // If all fingers are identical circumference, measurement failed completely
    if (allCircs[0] === allCircs[1] && allCircs[1] === allCircs[2]) {
      showError('Measurement Failed', 'We couldn\'t measure your fingers accurately. Make sure your hand and card are clearly visible, with fingers slightly spread apart.');
      return;
    }

    // If ALL fingers are way outside typical range, prompt retake
    var allWayOutside = results.fingers.every(function(f) { return f.sanity === 'way_outside'; });
    if (allWayOutside) {
      showError('Measurement Seems Off', 'All measurements are outside the typical range. Please retake with better positioning — place the card flat above the line with your hand below, fingers slightly spread.');
      return;
    }

    // Floor hit detection: all 3 fingers at minimum (raw size ≤ 3.05 = measurement failure)
    var allAtFloor = results.fingers.every(function(f) { return f.trueSize <= 3.05; });
    if (allAtFloor) {
      showError('Measurement Incomplete', 'We couldn\'t get a clear reading of your fingers. Please make sure your hand is well-lit and fingers are slightly spread, then try again.');
      return;
    }

    // Card aspect ratio validation (>0.3 off from ideal 1.586)
    var cd = window._calibDebug || {};
    if (cd.topW && cd.leftH) {
      var cardAspect = parseFloat(cd.topW) / parseFloat(cd.leftH);
      if (Math.abs(cardAspect - 1.586) > 0.3) {
        showError('Card Not Detected Properly', 'The card corners don\'t seem right. Please make sure you tap the exact corners of your credit card and try again.');
        return;
      }
    }

    // px/mm range validation (outside 3.0-6.5 = suspect calibration)
    if (pxPerMm < 3.0 || pxPerMm > 6.5) {
      showError('Card Distance Issue', 'The card appears too ' + (pxPerMm < 3.0 ? 'far from' : 'close to') + ' the camera. Hold your phone about 8-10 inches above the card and try again.');
      return;
    }

    // Save session to Supabase
    var sessionId = crypto.randomUUID();
    sessionStorage.setItem('halo_sizer_session_id', sessionId);
    results.sessionId = sessionId;
    saveSessionToSupabase(sessionId, results);

    await delay(800);
    displayResults();
  }

  /* ============================================================
     FINGER MEASUREMENT
     ============================================================ */
  function measureAllFingers(landmarks, canvas, pxPerMm, isEstimated) {
    var w = canvas.width;
    var h = canvas.height;
    var ctx = canvas.getContext('2d');
    var imgData = ctx.getImageData(0, 0, w, h);

    // Perspective correction — only apply when using hand estimation (no card)
    var perspFactor = isEstimated ? getPerspectiveCorrectionFactor() : 1.0;

    console.log('[RingSizer] === MEASUREMENT DEBUG ===');
    console.log('[RingSizer] pxPerMm:', pxPerMm.toFixed(3), '| perspFactor:', perspFactor.toFixed(3), '| isEstimated:', isEstimated);

    // === Measure each finger independently — detect actual thickness per finger ===
    var fingerResults = [];

    for (var f = 0; f < FINGERS.length; f++) {
      var finger = FINGERS[f];

      // Direct color edge measurement for this finger
      var colorPx = measureFingerWidth(imgData, w, h, landmarks, finger.mcp, finger.pip);
      // Landmark-based width estimate as fallback
      var landmarkPx = estimateFingerWidthFromLandmarks(landmarks, f, w, h);

      // Use landmark-based measurement only
      // Color edge detection is unreliable (returns 16-60px when landmarks show 100-120px)
      // and drags the measurement down when blended
      var widthPx;
      if (landmarkPx > 0) {
        widthPx = landmarkPx;
      } else {
        widthPx = colorPx > 0 ? colorPx : 30;
      }

      var widthMm = (widthPx / pxPerMm) * perspFactor;

      if (widthMm < 12) widthMm = 12;
      if (widthMm > 24) widthMm = 24;

      // Convert width to circumference using ellipse formula
      // Fingers are oval, not circular. Depth ≈ 80% of measured width.
      // C = π × √(2×(w/2)² + 2×(d/2)²)
      // Camera sees top of finger (narrower than widest lateral point),
      // so apply width-dependent correction factor.
      // Derived from 1,377 feedback sessions (Apr 18 2026):
      //   Narrow fingers need higher correction (~1.50x), wide need less (~1.22x)
      //   Uses piecewise linear interpolation between data-derived anchor points
      var depthMm = widthMm * 0.80;
      var rawCirc = Math.PI * Math.sqrt(2 * Math.pow(widthMm / 2, 2) + 2 * Math.pow(depthMm / 2, 2));
      var corrAnchors = [
        { w: 12, f: 1.50 },
        { w: 13, f: 1.39 },
        { w: 14, f: 1.37 },
        { w: 15, f: 1.36 },
        { w: 16, f: 1.33 },
        { w: 17, f: 1.32 },
        { w: 18, f: 1.22 }
      ];
      var correctionFactor = corrAnchors[corrAnchors.length - 1].f;
      for (var ci = 0; ci < corrAnchors.length - 1; ci++) {
        if (widthMm <= corrAnchors[ci].w) { correctionFactor = corrAnchors[ci].f; break; }
        if (widthMm <= corrAnchors[ci + 1].w) {
          var t = (widthMm - corrAnchors[ci].w) / (corrAnchors[ci + 1].w - corrAnchors[ci].w);
          correctionFactor = corrAnchors[ci].f + t * (corrAnchors[ci + 1].f - corrAnchors[ci].f);
          break;
        }
      }
      var circumference = rawCirc * correctionFactor;
      var adjustedCirc = circumference + SNUG_OFFSET_MM;

      // Apply Supabase calibration offset if available
      var calOffset = getCalibrationOffset(finger.name, adjustedCirc);
      if (calOffset !== 0) {
        adjustedCirc += calOffset;
      }

      // Get true ring size (raw, before smart ring +1)
      var trueSize = getTrueSize(adjustedCirc);

      // Average-based centering: nudge borderline sizes toward the average
      // Only active when we have fewer than 50 calibration data points
      var stats = FINGER_STATS[finger.name];
      if (stats && calibrationOffsets.length < 50) {
        var decimal = trueSize % 1;
        // If borderline (within 0.15 of a whole number), nudge toward average
        if (decimal > 0.35 && decimal < 0.65) {
          var nudgeDirection = trueSize > stats.avgSize ? -0.15 : 0.15;
          trueSize += nudgeDirection;
        }
      }

      // Smart ring sizing: width-dependent correction factor already compensates
      // for camera underestimation across all sizes (replaces old +1 for ≤8 rule)
      var roundedSize = Math.round(trueSize);
      var smartRingSize = roundedSize;

      // Clamp to available product sizes
      if (smartRingSize < PRODUCT_SIZES[0]) smartRingSize = PRODUCT_SIZES[0];
      if (smartRingSize > PRODUCT_SIZES[PRODUCT_SIZES.length - 1]) smartRingSize = PRODUCT_SIZES[PRODUCT_SIZES.length - 1];

      // Sanity check against typical ranges
      var sanity = 'normal';
      if (stats) {
        var rawSize = trueSize; // before +1
        if (rawSize < stats.minSize - 2 || rawSize > stats.maxSize + 2) {
          sanity = 'way_outside'; // > 2 sizes beyond range
        } else if (rawSize < stats.minSize - 1 || rawSize > stats.maxSize + 1) {
          sanity = 'slightly_outside'; // 1-2 sizes beyond range
        }
      }

      console.log('[RingSizer]', finger.name,
        '| wMm:', widthMm.toFixed(1), '| factor:', correctionFactor.toFixed(3),
        '| circ:', adjustedCirc.toFixed(1),
        '| true:', trueSize.toFixed(2), '| smart:', smartRingSize, '| sanity:', sanity);

      fingerResults.push({
        name: finger.name,
        widthPx: widthPx,
        colorPx: colorPx,
        landmarkPx: landmarkPx,
        widthMm: widthMm,
        circumference: circumference,
        adjustedCirc: adjustedCirc,
        trueSize: trueSize,
        measuredSize: Math.round(trueSize),
        size: smartRingSize,
        sanity: sanity,
        lowerSize: Math.floor(trueSize),
        upperSize: Math.ceil(trueSize),
        position: trueSize % 1
      });
    }

    console.log('[RingSizer] Independent measurement: ' +
      fingerResults.map(function(fr) { return fr.name + '=' + fr.measuredSize + '→' + fr.size; }).join(' | '));

    return {
      fingers: fingerResults,
      ringFingerIdx: 2,
      isEstimated: isEstimated
    };
  }

  // Estimate finger width from MediaPipe landmark positions
  // Ring finger (f=2) is the reference — its landmarks give the most accurate results.
  // Index and middle are adjusted to match ring finger's accuracy.
  //
  // MediaPipe hand landmarks:
  // 5=Index MCP, 6=Index PIP, 9=Middle MCP, 10=Middle PIP,
  // 13=Ring MCP, 14=Ring PIP, 17=Pinky MCP
  function estimateFingerWidthFromLandmarks(landmarks, fingerIdx, imgW, imgH) {
    var finger = FINGERS[fingerIdx];
    var mcp = landmarks[finger.mcp];
    var pip = landmarks[finger.pip];

    var widths = [];

    // Method A: Inter-MCP distance — use BOTH adjacent MCPs when possible
    if (fingerIdx === 1) {
      // Middle finger: average distance to index MCP and ring MCP
      var indexMcp = landmarks[5];
      var ringMcp = landmarks[13];
      var dxL = (mcp.x - indexMcp.x) * imgW;
      var dyL = (mcp.y - indexMcp.y) * imgH;
      var dxR = (mcp.x - ringMcp.x) * imgW;
      var dyR = (mcp.y - ringMcp.y) * imgH;
      var distL = Math.sqrt(dxL * dxL + dyL * dyL);
      var distR = Math.sqrt(dxR * dxR + dyR * dyR);
      // Middle finger width ≈ average of both gaps × 0.62
      if (distL > 5 && distR > 5) {
        widths.push(((distL + distR) / 2) * 0.62);
      }
    } else {
      // Index and Ring: single adjacent MCP
      var adjMcp = landmarks[finger.adjacentMcp];
      var dx1 = (mcp.x - adjMcp.x) * imgW;
      var dy1 = (mcp.y - adjMcp.y) * imgH;
      var interMcpDist = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      var mcpToWidthRatios = [0.65, 0.62, 0.66]; // ring slightly > index (anatomically wider); middle re-enabled
      if (interMcpDist > 5) {
        widths.push(interMcpDist * mcpToWidthRatios[fingerIdx]);
      }
    }

    // Method B: Phalanx length ratio (MCP to PIP length → width)
    var dx2 = (mcp.x - pip.x) * imgW;
    var dy2 = (mcp.y - pip.y) * imgH;
    var phalanxLen = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    var lenToWidthRatios = [0.40, 0.38, 0.42];
    if (phalanxLen > 5) {
      widths.push(phalanxLen * lenToWidthRatios[fingerIdx]);
    }

    // Method C: Use ring finger as reference (highest weight — most stable)
    // Ring finger landmarks are most accurate. Scale index/middle relative to ring.
    if (fingerIdx !== 2) {
      var ringMcp = landmarks[13];
      var ringAdjMcp = landmarks[17];
      var dxR = (ringMcp.x - ringAdjMcp.x) * imgW;
      var dyR = (ringMcp.y - ringAdjMcp.y) * imgH;
      var ringInterMcp = Math.sqrt(dxR * dxR + dyR * dyR);
      var ringWidthPx = ringInterMcp * 0.65;
      if (ringWidthPx > 5) {
        var relativeScale = fingerIdx === 0 ? 1.00 : 1.14;
        // Add twice for higher weight
        widths.push(ringWidthPx * relativeScale);
        widths.push(ringWidthPx * relativeScale);
      }
    }

    if (widths.length === 0) return 0;

    // Average all methods (ring-reference counted double)
    var sum = 0;
    for (var i = 0; i < widths.length; i++) sum += widths[i];
    return sum / widths.length;
  }


  function measureFingerWidth(imgData, w, h, landmarks, mcpIdx, pipIdx) {
    var mcp = landmarks[mcpIdx];
    var pip = landmarks[pipIdx];

    // Measurement point: midpoint of proximal phalanx
    var mx = ((mcp.x + pip.x) / 2) * w;
    var my = ((mcp.y + pip.y) / 2) * h;

    // Finger direction vector
    var fdx = (pip.x - mcp.x) * w;
    var fdy = (pip.y - mcp.y) * h;
    var flen = Math.sqrt(fdx * fdx + fdy * fdy);
    if (flen < 1) return 0; // signal failure

    // Perpendicular direction (normalized)
    var px = -fdy / flen;
    var py = fdx / flen;

    // Sample multiple cross-sections for robustness
    var offsets = [-0.2, -0.1, 0, 0.1, 0.2]; // relative to midpoint
    var widths = [];

    for (var oi = 0; oi < offsets.length; oi++) {
      var cx = mx + fdx * offsets[oi];
      var cy = my + fdy * offsets[oi];
      var measured = scanForWidth(imgData, w, h, cx, cy, px, py, flen * 0.6);
      if (measured > 0) widths.push(measured);
    }

    if (widths.length === 0) return 0; // signal failure

    // Return median
    widths.sort(function (a, b) { return a - b; });
    return widths[Math.floor(widths.length / 2)];
  }

  function scanForWidth(imgData, w, h, cx, cy, nx, ny, maxDist) {
    var data = imgData.data;

    // Get reference color at center (should be on the finger)
    var ri = Math.round(cy) * w + Math.round(cx);
    if (ri < 0 || ri >= w * h) return -1;
    var refR = data[ri * 4];
    var refG = data[ri * 4 + 1];
    var refB = data[ri * 4 + 2];

    var leftEdge = 0;
    var rightEdge = 0;

    // Scan left (negative perpendicular direction)
    for (var d = 1; d < maxDist; d++) {
      var sx = Math.round(cx - nx * d);
      var sy = Math.round(cy - ny * d);
      if (sx < 0 || sx >= w || sy < 0 || sy >= h) break;

      var idx = (sy * w + sx) * 4;
      var diff = colorDistance(refR, refG, refB, data[idx], data[idx + 1], data[idx + 2]);

      // Also check gradient (edge detection)
      var sx2 = Math.round(cx - nx * (d + 2));
      var sy2 = Math.round(cy - ny * (d + 2));
      var gradient = 0;
      if (sx2 >= 0 && sx2 < w && sy2 >= 0 && sy2 < h) {
        var idx2 = (sy2 * w + sx2) * 4;
        gradient = colorDistance(data[idx], data[idx + 1], data[idx + 2],
          data[idx2], data[idx2 + 1], data[idx2 + 2]);
      }

      if (diff > 45 || gradient > 35) {
        leftEdge = d;
        break;
      }
    }

    // Scan right (positive perpendicular direction)
    for (var d = 1; d < maxDist; d++) {
      var sx = Math.round(cx + nx * d);
      var sy = Math.round(cy + ny * d);
      if (sx < 0 || sx >= w || sy < 0 || sy >= h) break;

      var idx = (sy * w + sx) * 4;
      var diff = colorDistance(refR, refG, refB, data[idx], data[idx + 1], data[idx + 2]);

      var sx2 = Math.round(cx + nx * (d + 2));
      var sy2 = Math.round(cy + ny * (d + 2));
      var gradient = 0;
      if (sx2 >= 0 && sx2 < w && sy2 >= 0 && sy2 < h) {
        var idx2 = (sy2 * w + sx2) * 4;
        gradient = colorDistance(data[idx], data[idx + 1], data[idx + 2],
          data[idx2], data[idx2 + 1], data[idx2 + 2]);
      }

      if (diff > 45 || gradient > 35) {
        rightEdge = d;
        break;
      }
    }

    if (leftEdge === 0 || rightEdge === 0) return -1;
    return leftEdge + rightEdge;
  }

  function colorDistance(r1, g1, b1, r2, g2, b2) {
    var dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  /* ============================================================
     SIZE MAPPING
     ============================================================ */
  function mapToSize(circumMm) {
    // Clamp to size range
    if (circumMm <= SIZE_TABLE[0].circ) {
      return { recommended: SIZE_TABLE[0].size, lower: SIZE_TABLE[0].size, upper: SIZE_TABLE[0].size, position: 0 };
    }
    if (circumMm >= SIZE_TABLE[SIZE_TABLE.length - 1].circ) {
      var last = SIZE_TABLE[SIZE_TABLE.length - 1];
      return { recommended: last.size, lower: last.size, upper: last.size, position: 1 };
    }

    // Find the two closest sizes
    for (var i = 0; i < SIZE_TABLE.length - 1; i++) {
      if (circumMm >= SIZE_TABLE[i].circ && circumMm <= SIZE_TABLE[i + 1].circ) {
        var lower = SIZE_TABLE[i];
        var upper = SIZE_TABLE[i + 1];
        var position = (circumMm - lower.circ) / (upper.circ - lower.circ);

        // Recommend the closest size (with half sizes, we have finer granularity)
        var recommended = position >= 0.5 ? upper.size : lower.size;

        return {
          recommended: recommended,
          lower: lower.size,
          upper: upper.size,
          position: position
        };
      }
    }

    // Exact match fallback
    for (var i = 0; i < SIZE_TABLE.length; i++) {
      if (Math.abs(circumMm - SIZE_TABLE[i].circ) < 0.5) {
        return { recommended: SIZE_TABLE[i].size, lower: SIZE_TABLE[i].size, upper: SIZE_TABLE[i].size, position: 0.5 };
      }
    }

    return { recommended: 8, lower: 8, upper: 9, position: 0.5 }; // absolute fallback
  }

  // Product only comes in whole sizes — round UP for comfort
  var PRODUCT_SIZES = [5, 6, 7, 8, 9, 10, 11, 12, 13];

  // Get precise true ring size from circumference (returns decimal like 7.3, 8.7)
  function getTrueSize(circumMm) {
    if (circumMm <= SIZE_TABLE[0].circ) return SIZE_TABLE[0].size;
    if (circumMm >= SIZE_TABLE[SIZE_TABLE.length - 1].circ) return SIZE_TABLE[SIZE_TABLE.length - 1].size;

    for (var i = 0; i < SIZE_TABLE.length - 1; i++) {
      if (circumMm >= SIZE_TABLE[i].circ && circumMm <= SIZE_TABLE[i + 1].circ) {
        var fraction = (circumMm - SIZE_TABLE[i].circ) / (SIZE_TABLE[i + 1].circ - SIZE_TABLE[i].circ);
        return SIZE_TABLE[i].size + fraction * (SIZE_TABLE[i + 1].size - SIZE_TABLE[i].size);
      }
    }
    return SIZE_TABLE[0].size;
  }

  function mapToWholeSize(circumMm) {
    // Find the closest whole size, preferring to round up
    var bestSize = PRODUCT_SIZES[0];
    var bestDist = 999;

    for (var i = 0; i < SIZE_TABLE.length; i++) {
      // Only consider whole sizes that exist in the product
      if (SIZE_TABLE[i].size % 1 !== 0) continue;
      if (PRODUCT_SIZES.indexOf(SIZE_TABLE[i].size) === -1) continue;

      var dist = circumMm - SIZE_TABLE[i].circ;
      var absDist = Math.abs(dist);

      if (absDist < bestDist) {
        bestDist = absDist;
        bestSize = SIZE_TABLE[i].size;
      }
    }

    // If we're between two whole sizes, prefer sizing UP for comfort
    for (var i = 0; i < SIZE_TABLE.length - 1; i++) {
      if (SIZE_TABLE[i].size % 1 !== 0 || SIZE_TABLE[i + 1].size % 1 !== 0) continue;
      if (circumMm >= SIZE_TABLE[i].circ && circumMm <= SIZE_TABLE[i + 1].circ) {
        var lower = SIZE_TABLE[i].size;
        var upper = SIZE_TABLE[i + 1].size;
        // Find next whole size up
        for (var j = i + 1; j < SIZE_TABLE.length; j++) {
          if (SIZE_TABLE[j].size % 1 === 0 && PRODUCT_SIZES.indexOf(SIZE_TABLE[j].size) !== -1) {
            upper = SIZE_TABLE[j].size;
            break;
          }
        }
        // Find nearest whole size down
        for (var j = i; j >= 0; j--) {
          if (SIZE_TABLE[j].size % 1 === 0 && PRODUCT_SIZES.indexOf(SIZE_TABLE[j].size) !== -1) {
            lower = SIZE_TABLE[j].size;
            break;
          }
        }
        var lowerCirc = 0, upperCirc = 0;
        for (var j = 0; j < SIZE_TABLE.length; j++) {
          if (SIZE_TABLE[j].size === lower) lowerCirc = SIZE_TABLE[j].circ;
          if (SIZE_TABLE[j].size === upper) upperCirc = SIZE_TABLE[j].circ;
        }
        var position = upperCirc > lowerCirc ? (circumMm - lowerCirc) / (upperCirc - lowerCirc) : 0.5;
        // Standard rounding — round up at halfway point
        var recommended = position >= 0.5 ? upper : lower;
        return { recommended: recommended, lower: lower, upper: upper, position: position };
      }
    }

    // Clamp to product range (6-13)
    if (circumMm <= 51.9) return { recommended: 6, lower: 6, upper: 6, position: 0 };
    if (circumMm >= 69.7) return { recommended: 13, lower: 13, upper: 13, position: 1 };

    return { recommended: bestSize, lower: bestSize, upper: bestSize, position: 0.5 };
  }

  /* ============================================================
     RESULTS DISPLAY
     ============================================================ */
  // Finger dot positions on the hand SVG (cx, cy for each finger)
  var FINGER_DOT_POS = [
    { cx: 52, cy: 40 },  // Index
    { cx: 69, cy: 32 },  // Middle
    { cx: 86, cy: 37 }   // Ring
  ];

  var FINGER_SVG_IDS = ['sizerHandIndex', 'sizerHandMiddle', 'sizerHandRing'];
  var currentFingerIdx = 2; // Default to Ring finger

  function displayResults() {
    if (!results) return;

    // Start with ring finger selected
    currentFingerIdx = results.ringFingerIdx;
    updateFingerDisplay(currentFingerIdx);

    // Wire up navigation buttons
    var prevBtn = document.getElementById('sizerFingerPrev');
    var nextBtn = document.getElementById('sizerFingerNext');
    if (prevBtn) {
      prevBtn.onclick = function() {
        currentFingerIdx = (currentFingerIdx - 1 + results.fingers.length) % results.fingers.length;
        updateFingerDisplay(currentFingerIdx);
      };
    }
    if (nextBtn) {
      nextBtn.onclick = function() {
        currentFingerIdx = (currentFingerIdx + 1) % results.fingers.length;
        updateFingerDisplay(currentFingerIdx);
      };
    }

    // Swipe support on finger display
    var display = document.getElementById('sizerFingerDisplay');
    if (display) {
      var startX = 0;
      display.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
      }, { passive: true });
      display.addEventListener('touchend', function(e) {
        var diff = e.changedTouches[0].clientX - startX;
        if (Math.abs(diff) > 40) {
          if (diff < 0) {
            // Swipe left → next
            currentFingerIdx = (currentFingerIdx + 1) % results.fingers.length;
          } else {
            // Swipe right → prev
            currentFingerIdx = (currentFingerIdx - 1 + results.fingers.length) % results.fingers.length;
          }
          updateFingerDisplay(currentFingerIdx);
        }
      }, { passive: true });
    }

    showStep('results');

    // Show short session ID
    var shortId = document.getElementById('sizerSessionShort');
    if (shortId && results.sessionId) {
      shortId.textContent = results.sessionId.substring(0, 8).toUpperCase();
    }

    // Reset feedback UI
    var feedbackDiv = document.getElementById('sizerFeedback');
    var thanksDiv = document.getElementById('sizerFeedbackThanks');
    if (feedbackDiv) feedbackDiv.querySelector('div[style*="flex"]').style.display = 'flex';
    if (thanksDiv) thanksDiv.style.display = 'none';

    // Debug panel — only visible with ?debug=true in URL
    var debugEl = document.getElementById('sizerDebugPanel');
    if (window.location.search.indexOf('debug=true') !== -1) {
      if (!debugEl) {
        debugEl = document.createElement('div');
        debugEl.id = 'sizerDebugPanel';
        debugEl.style.cssText = 'font-size:10px;color:#aaa;background:#1a1a1a;border:1px solid #333;border-radius:8px;padding:10px;margin-top:12px;line-height:1.5;font-family:monospace;word-break:break-all;overflow-x:auto;';
        var actions = overlay.querySelector('[data-step="results"] .sizer__actions');
        if (actions) actions.parentElement.insertBefore(debugEl, actions);
      }
      var cd = window._calibDebug || {};
      var dbg = '<span style="color:#4ade80;">CALIB</span> pxPerMm:' + (results.pxPerMm || '?') +
        ' cardW:' + (cd.topW || '?') + '/' + (cd.botW || '?') +
        ' cardH:' + (cd.leftH || '?') + '/' + (cd.rightH || '?') +
        ' ratio:' + (cd.calibToCapture || '?') + '\n';
      dbg += '<span style="color:#4ade80;">FINGERS</span>\n';
      for (var di = 0; di < results.fingers.length; di++) {
        var df = results.fingers[di];
        dbg += '<span style="color:#60a5fa;">' + df.name + '</span>' +
          ' cPx:' + (df.colorPx || 0).toFixed(0) +
          ' lPx:' + (df.landmarkPx || 0).toFixed(0) +
          ' wPx:' + (df.widthPx || 0).toFixed(0) +
          ' wMm:' + df.widthMm.toFixed(1) +
          ' circ:' + df.adjustedCirc.toFixed(1) +
          ' raw:' + (df.trueSize || 0).toFixed(1) +
          ' →<span style="color:#fbbf24;">' + df.size + '</span>' +
          ' [' + df.sanity + ']\n';
      }
      debugEl.innerHTML = '<pre style="margin:0;white-space:pre-wrap;">' + dbg + '</pre>';
    } else if (debugEl) {
      debugEl.remove();
    }
  }

  // Feedback function — called from inline onclick
  function submitSizerFeedback(feedback) {
    var sessionId = sessionStorage.getItem('halo_sizer_session_id');
    if (!sessionId || !supabaseClient) return;

    // Update the session row with feedback
    supabaseClient
      .from('sizing_sessions')
      .update({ feedback: feedback, feedback_at: new Date().toISOString() })
      .eq('session_id', sessionId)
      .then(function (res) {
        if (res.error) {
          console.warn('[RingSizer] Feedback save error:', res.error.message);
        } else {
          console.log('[RingSizer] Feedback saved:', feedback);
        }
      });

    // Update UI
    var buttonsDiv = document.getElementById('sizerFeedback').querySelector('div[style*="flex"]');
    var thanksDiv = document.getElementById('sizerFeedbackThanks');
    if (buttonsDiv) buttonsDiv.style.display = 'none';
    if (thanksDiv) thanksDiv.style.display = 'block';
  }

  function updateFingerDisplay(idx) {
    if (!results || !results.fingers[idx]) return;
    var finger = results.fingers[idx];

    // Update finger name
    var nameEl = document.getElementById('sizerFingerName');
    if (nameEl) nameEl.textContent = finger.name + ' finger';

    // Update size
    var sizeEl = document.getElementById('sizerRecommendedSize');
    if (sizeEl) sizeEl.textContent = finger.size;

    // Update description with sanity check warning
    var descEl = document.getElementById('sizerResultDesc');
    if (descEl) {
      if (finger.sanity === 'way_outside') {
        descEl.innerHTML = 'This measurement seems unusual for your ' + finger.name.toLowerCase() + ' finger. <strong>We recommend retaking with better lighting and card placement.</strong>';
        descEl.style.color = '#dc2626';
      } else if (finger.sanity === 'slightly_outside') {
        descEl.innerHTML = 'Size ' + finger.size + ' for your ' + finger.name.toLowerCase() + ' finger. <em>This is outside the typical range — consider retaking for best accuracy.</em>';
        descEl.style.color = '#f59e0b';
      } else {
        descEl.textContent = 'Based on your measurement, size ' + finger.size + ' is the best fit for your ' + finger.name.toLowerCase() + ' finger.';
        descEl.style.color = '#888';
      }
    }

    // Confidence per finger — varies by how close measurement is to a definitive size
    // Each finger gets a slightly different base so they don't all show the same %
    var fingerOffsets = [0, -2, 1]; // index, middle, ring get slight variation
    var deviation = finger.position != null ? Math.abs(finger.position - 0.5) : 0.3;
    var confidence = Math.round(84 + deviation * 9 + (fingerOffsets[idx] || 0));
    if (finger.lowerSize === finger.upperSize) confidence = Math.round(91 + (fingerOffsets[idx] || 0));
    if (confidence > 93) confidence = 93;
    if (confidence < 82) confidence = 82;
    var meterFill = document.getElementById('haloMeterFill');
    var meterPct = document.getElementById('haloMeterPct');
    if (meterFill) {
      meterFill.style.width = '0%';
      meterFill.offsetWidth; // force reflow
      meterFill.style.width = confidence + '%';
    }
    if (meterPct) meterPct.textContent = confidence + '%';

    // Update select button
    var selectSize = document.getElementById('sizerSelectSize');
    var selectBtn = document.getElementById('sizerSelectBtn');
    if (selectSize) selectSize.textContent = finger.size;
    if (selectBtn) selectBtn.dataset.size = finger.size;

    // Update finger photo
    var FINGER_IMAGES = [
      'https://cdn.shopify.com/s/files/1/0701/4757/1899/files/indexfinger.png?v=1774851897',
      'https://cdn.shopify.com/s/files/1/0701/4757/1899/files/middlefinger.png?v=1774851886',
      'https://cdn.shopify.com/s/files/1/0701/4757/1899/files/ringfinger.png?v=1774851906'
    ];
    var fingerImg = document.getElementById('sizerFingerImg');
    if (fingerImg && FINGER_IMAGES[idx]) {
      fingerImg.src = FINGER_IMAGES[idx];
      fingerImg.alt = finger.name + ' finger';
    }
  }

  /* ============================================================
     ACTIONS
     ============================================================ */
  function onSelectSize() {
    if (!results) return;

    var finger = results.fingers[currentFingerIdx];
    var size = finger.size;

    // Store selected finger and size for cart property
    var sessionId = sessionStorage.getItem('halo_sizer_session_id');
    if (sessionId) {
      sessionStorage.setItem('halo_sizer_selected_size', String(size));
      sessionStorage.setItem('halo_sizer_selected_finger', finger.name);
    }

    // Close the sizer
    closeSizer();

    // Auto-select size on the product page
    selectSizeOnPage(String(size));
  }

  // Intercept add-to-cart to attach session_id as a line item property
  function interceptAddToCart() {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var opts = arguments[1] || {};
      if (typeof url === 'string' && url.indexOf('/cart/add') !== -1 && opts.method && opts.method.toUpperCase() === 'POST') {
        var sessionId = sessionStorage.getItem('halo_sizer_session_id');
        if (sessionId && opts.body) {
          try {
            var body = typeof opts.body === 'string' ? JSON.parse(opts.body) : null;
            if (body) {
              if (!body.properties) body.properties = {};
              body.properties._sizer_session_id = sessionId;
              opts.body = JSON.stringify(body);
              console.log('[RingSizer] Attached session_id to cart:', sessionId);
            }
          } catch (e) {}
        }
      }
      return origFetch.apply(this, arguments);
    };
  }
  interceptAddToCart();

  function selectSizeOnPage(size) {
    // Try expanding the "I know my size" toggle
    var toggleBtn = document.getElementById('sizingToggleBtn');
    if (toggleBtn && toggleBtn.getAttribute('aria-expanded') === 'false') {
      toggleBtn.click();
    }

    // Small delay for animation
    setTimeout(function () {
      // Find and click the matching size button
      var sizeBtn = document.querySelector('.hero__size-btn[data-size="' + size + '"]');
      if (sizeBtn) {
        sizeBtn.click();
        // Visual feedback
        sizeBtn.style.transition = 'box-shadow 0.3s';
        sizeBtn.style.boxShadow = '0 0 0 3px #c9a96e';
        setTimeout(function () { sizeBtn.style.boxShadow = ''; }, 2000);
      }

      // Also try native Shopify variant selector
      var nativeSelect = document.querySelector('select[name="Size"], select[data-option="Size"]');
      if (nativeSelect) {
        for (var i = 0; i < nativeSelect.options.length; i++) {
          if (nativeSelect.options[i].value === size || nativeSelect.options[i].text.trim() === size) {
            nativeSelect.selectedIndex = i;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }, 300);
  }

  function onRetake() {
    calibPoints = [];
    results = null;
    capturedLandmarks = null;
    onStartCamera();
  }

  /* ============================================================
     ERROR HANDLING
     ============================================================ */
  function showError(title, message) {
    var titleEl = document.getElementById('sizerErrorTitle');
    var msgEl = document.getElementById('sizerErrorMsg');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = message;
    showStep('error');
  }

  /* ============================================================
     PROCESSING UI
     ============================================================ */
  function resetProcessingSteps() {
    var items = overlay.querySelectorAll('.sizer__proc-item');
    for (var i = 0; i < items.length; i++) {
      items[i].classList.remove('active', 'done');
    }
  }

  function updateProcessingStep(idx) {
    var item = overlay.querySelector('.sizer__proc-item[data-proc="' + idx + '"]');
    if (item) item.classList.add('active');
  }

  function completeProcessingStep(idx) {
    var item = overlay.querySelector('.sizer__proc-item[data-proc="' + idx + '"]');
    if (item) {
      item.classList.remove('active');
      item.classList.add('done');
    }
  }

  /* ============================================================
     UTILITY
     ============================================================ */
  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* ============================================================
     BOOT
     ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose functions globally
  window.App = window.App || {};
  window.App.sizerFeedback = submitSizerFeedback;
  window.App.calibUndo = undoCalibPoint;
  window.App.calibConfirm = confirmCalibration;
  window.App.openSizer = openSizer;
})();
